"use strict";
/**
 * Manual Price Controls
 *
 * Manages the manual-price overlay on the order page:
 * - Yes / No price range sliders
 * - Numeric text inputs with ±1 step buttons
 * - "Selected" state toggling (clearing the input deselects the price)
 *
 * The manual price is stored as an integer 1–99 in `TradingSettings`
 * and persisted to sessionStorage via `saveSettingsToStorage`.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.setupManualPriceControls = setupManualPriceControls;
exports.initManualPriceState = initManualPriceState;
const state_1 = require("../../state");
const ui_1 = require("../../ui");
const sport_mode_1 = require("../../ui/sport-mode");
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
/**
 * Update a manual price for the given side, clamp to [1, 99], persist, and
 * refresh the UI slider/input.
 *
 * @param side       - "yes" or "no"
 * @param newPrice   - Raw price value (will be clamped)
 * @param isSelected - Whether this side's price is actively selected
 */
function setManualPrice(side, newPrice, isSelected = true) {
    const clamped = Math.max(1, Math.min(99, newPrice));
    const state = (0, state_1.getState)();
    const key = side === "yes" ? "manualYesPrice" : "manualNoPrice";
    const selectedKey = side === "yes" ? "manualYesPriceSelected" : "manualNoPriceSelected";
    const updatedSettings = {
        ...state.tradingSettings,
        [key]: clamped,
        [selectedKey]: isSelected,
    };
    (0, state_1.updateState)({ tradingSettings: updatedSettings });
    (0, state_1.saveSettingsToStorage)();
    (0, ui_1.updateManualPriceSlider)(side, clamped, isSelected);
}
// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
/**
 * Wire up all manual-price controls (sliders, text inputs, ±1 buttons).
 *
 * Call once during `setupOrderPage`.
 */
function setupManualPriceControls() {
    // Range sliders
    const manualYesSlider = document.getElementById("manual-yes-price-slider");
    const manualNoSlider = document.getElementById("manual-no-price-slider");
    if (manualYesSlider) {
        manualYesSlider.addEventListener("input", (e) => {
            setManualPrice("yes", parseInt(e.target.value, 10));
        });
    }
    if (manualNoSlider) {
        manualNoSlider.addEventListener("input", (e) => {
            setManualPrice("no", parseInt(e.target.value, 10));
        });
    }
    // Numeric text inputs
    const manualYesInput = document.getElementById("manual-yes-price-input");
    const manualNoInput = document.getElementById("manual-no-price-input");
    if (manualYesInput) {
        manualYesInput.addEventListener("change", () => {
            if (manualYesInput.value.trim() === "") {
                setManualPrice("yes", (0, state_1.getState)().tradingSettings.manualYesPrice, false);
                return;
            }
            const val = parseInt(manualYesInput.value, 10);
            setManualPrice("yes", isNaN(val) ? 50 : val, true);
        });
    }
    if (manualNoInput) {
        manualNoInput.addEventListener("change", () => {
            if (manualNoInput.value.trim() === "") {
                setManualPrice("no", (0, state_1.getState)().tradingSettings.manualNoPrice, false);
                return;
            }
            const val = parseInt(manualNoInput.value, 10);
            setManualPrice("no", isNaN(val) ? 50 : val, true);
        });
    }
    // ±1 step buttons
    const manualYesDec = document.getElementById("manual-yes-decrement-btn");
    const manualYesInc = document.getElementById("manual-yes-increment-btn");
    const manualNoDec = document.getElementById("manual-no-decrement-btn");
    const manualNoInc = document.getElementById("manual-no-increment-btn");
    manualYesDec?.addEventListener("click", () => {
        const current = (0, state_1.getState)().tradingSettings.manualYesPrice;
        setManualPrice("yes", current - 1);
    });
    manualYesInc?.addEventListener("click", () => {
        const current = (0, state_1.getState)().tradingSettings.manualYesPrice;
        setManualPrice("yes", current + 1);
    });
    manualNoDec?.addEventListener("click", () => {
        const current = (0, state_1.getState)().tradingSettings.manualNoPrice;
        setManualPrice("no", current - 1);
    });
    manualNoInc?.addEventListener("click", () => {
        const current = (0, state_1.getState)().tradingSettings.manualNoPrice;
        setManualPrice("no", current + 1);
    });
}
/**
 * Set the initial manual-price visibility and slider positions from
 * persisted settings. Call once during `setupOrderPage`.
 *
 * In combined mode (any sport layout + manual price both active), the global
 * manual-price slider is hidden in favour of per-button steppers. Under
 * `forceDefaultButtons` the traditional buttons use the global manual price,
 * so the overlay must stay visible.
 */
function initManualPriceState() {
    const { tradingSettings } = (0, state_1.getState)();
    const isCombined = (0, sport_mode_1.isCombinedPricingMode)(tradingSettings);
    (0, ui_1.updateManualPriceVisibility)(tradingSettings.manualPriceMode === true && !isCombined);
    (0, ui_1.updateManualPriceSlider)("yes", tradingSettings.manualYesPrice, tradingSettings.manualYesPriceSelected);
    (0, ui_1.updateManualPriceSlider)("no", tradingSettings.manualNoPrice, tradingSettings.manualNoPriceSelected);
}
